import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { buildLocalNotificationPlan } from "./localPlan";
import type { NotificationIntent } from "./types";

export type LocalNotificationSyncStatus = "synced" | "denied" | "unsupported";

export interface LocalNotificationSyncResult {
  status: LocalNotificationSyncStatus;
  scheduled: number;
  cancelled: number;
  warnings: string[];
}

export type LocalNotificationPermissionStatus = "granted" | "denied" | "unsupported";

export async function requestNativeLocalNotificationPermission(): Promise<LocalNotificationPermissionStatus> {
  if (!Capacitor.isNativePlatform()) return "unsupported";

  let permission = await LocalNotifications.checkPermissions();
  if (permission.display !== "granted") {
    permission = await LocalNotifications.requestPermissions();
  }

  return permission.display === "granted" ? "granted" : "denied";
}

export async function reconcileNativeLocalNotifications(
  intents: readonly NotificationIntent[],
  now: Date = new Date(),
): Promise<LocalNotificationSyncResult> {
  if (!Capacitor.isNativePlatform()) {
    return {
      status: "unsupported",
      scheduled: 0,
      cancelled: 0,
      warnings: [],
    };
  }

  let permission = await LocalNotifications.checkPermissions();
  if (permission.display !== "granted") {
    permission = await LocalNotifications.requestPermissions();
  }

  if (permission.display !== "granted") {
    return {
      status: "denied",
      scheduled: 0,
      cancelled: 0,
      warnings: [],
    };
  }

  const pending = await LocalNotifications.getPending();
  const pendingIds = pending.notifications.map((notification) => notification.id);
  const plan = buildLocalNotificationPlan(intents, pendingIds, now);

  if (plan.cancelIds.length > 0) {
    await LocalNotifications.cancel({
      notifications: plan.cancelIds.map((id) => ({ id })),
    });
  }

  const warnings: string[] = [];

  if (plan.schedule.length > 0) {
    const scheduled = await LocalNotifications.schedule({
      notifications: plan.schedule.map(({ intent, nativeId, scheduledAt }) => ({
        id: nativeId,
        title: intent.title,
        body: intent.body,
        schedule: { at: scheduledAt },
        extra: {
          intentId: intent.id,
          notificationKey: intent.notificationKey,
          ...(intent.deepLink ? { deepLink: intent.deepLink } : {}),
        },
      })),
    });

    if (scheduled.warning?.message) {
      warnings.push(scheduled.warning.message);
    }
  }

  return {
    status: "synced",
    scheduled: plan.schedule.length,
    cancelled: plan.cancelIds.length,
    warnings,
  };
}
